import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import useSWR from "swr";
import {
  Button,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { fetcher } from "../../../../services/fetcher";

const DIFFICULTY_OPTIONS = [
  { value: "", label: "-- Select --" },
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "difficult", label: "Difficult" },
  { value: "extreme", label: "Extreme" },
];

const toCsv = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : item?.text || item?.title || ""))
      .filter(Boolean)
      .join(", ");
  }
  return typeof value === "string" ? value : "";
};

const mapProfileDefaults = (data) => ({
  cover: data?.cover != null ? String(data.cover) : "",
  subtitle: data?.subtitle || "",
  discount_label: data?.discount_label || "",
  promo_banner: data?.promo_banner || "",
  region: data?.region || "",
  activity_type: data?.activity_type || "",
  max_altitude: data?.max_altitude || "",
  altitude_band: data?.altitude_band || "",
  difficulty: data?.difficulty || "",
  season_window: data?.season_window || "",
  access_point: data?.access_point || "",
  terrain: data?.terrain || "",
  scope_description: data?.scope_description || "",
  stat_covered: data?.stat_covered || "",
  stat_evacuation: data?.stat_evacuation || "",
  stat_wilderness: data?.stat_wilderness || "",
  stat_settled_amount: data?.stat_settled_amount || "",
  pitch_text: data?.pitch_text || "",
  why_asc360_text: data?.why_asc360_text || "",
  why_asc360_bullets: toCsv(data?.why_asc360_bullets),
  key_features: toCsv(data?.key_features),
  important_notes: toCsv(data?.important_notes),
  conditional_notes: toCsv(data?.conditional_notes),
});

const toArray = (value) =>
  String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const sectionTitle = (label) => (
  <Text tt="uppercase" size="xs" fw={700} c="dimmed" mb="xs" mt="sm">
    {label}
  </Text>
);

const ExplorePlanProfileModalForm = ({
  opened,
  onClose,
  mode = "create",
  initialData,
  isSubmitting = false,
  onSubmit,
}) => {
  const form = useForm({
    defaultValues: mapProfileDefaults(initialData),
  });

  const { register, handleSubmit, reset, control } = form;

  // Load selectable covers only for this modal (scoped to Explore Plans sub-module).
  const coversApiUrl = opened
    ? "issuance/user-cover-plan-access/?policy_type=bulk&issue_as=agent"
    : null;
  const { data: coversData, isLoading: isCoversLoading } = useSWR(
    coversApiUrl,
    fetcher,
    { revalidateOnFocus: false }
  );

  const coverOptions = useMemo(() => {
    const covers = coversData?.covers;
    if (!Array.isArray(covers)) return [];
    return (
      covers.flatMap((cover) =>
        cover?.customize_covers?.map((item) => ({
          value: String(item.id),
          label:
            item?.title != null
              ? `${item.title} - ${item.price} ${item.currency}`
              : `Cover ${item.id}`,
        }))
      ) || []
    );
  }, [coversData]);

  useEffect(() => {
    if (opened && mode === "create") reset(mapProfileDefaults({}));
  }, [opened, mode, reset]);

  useEffect(() => {
    if (opened && mode === "edit") reset(mapProfileDefaults(initialData));
  }, [opened, mode, initialData, reset]);

  const submit = (values) => {
    const payload = {
      cover: Number(values.cover),
      subtitle: values.subtitle || "",
      discount_label: values.discount_label || "",
      promo_banner: values.promo_banner || "",
      region: values.region || "",
      activity_type: values.activity_type || "",
      max_altitude: values.max_altitude || "",
      altitude_band: values.altitude_band || "",
      difficulty: values.difficulty || "",
      season_window: values.season_window || "",
      access_point: values.access_point || "",
      terrain: values.terrain || "",
      scope_description: values.scope_description || "",
      stat_covered: values.stat_covered || "",
      stat_evacuation: values.stat_evacuation || "",
      stat_wilderness: values.stat_wilderness || "",
      stat_settled_amount: values.stat_settled_amount || "",
      pitch_text: values.pitch_text || "",
      why_asc360_text: values.why_asc360_text || "",
      why_asc360_bullets: toArray(values.why_asc360_bullets),
      key_features: toArray(values.key_features),
      important_notes: toArray(values.important_notes),
      conditional_notes: toArray(values.conditional_notes),
    };
    onSubmit(payload);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        mode === "edit" ? "Edit Explorer Profile" : "New Explorer Profile"
      }
      size="xl"
      radius="lg"
      centered
      overlayProps={{ backgroundOpacity: 0.45, blur: 6 }}
      transitionProps={{
        transition: "slide-down",
        duration: 280,
        timingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
      }}
      styles={{
        content: {
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 25px 50px -12px rgba(15, 23, 42, 0.14)",
        },
        header: {
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
          borderBottom: "1px solid #e2e8f0",
        },
        title: { color: "#092c4c", fontWeight: 600, fontSize: 18 },
        body: {
          maxHeight: "min(85vh, 880px)",
          overflowY: "auto",
          background: "#ffffff",
        },
      }}
    >
      <form onSubmit={handleSubmit(submit)}>
        <Stack gap="md">
          <Controller
            name="cover"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                label="Cover Plan"
                placeholder="Select cover plan"
                withAsterisk
                data={coverOptions}
                searchable
                clearable
                value={field.value ? String(field.value) : ""}
                onChange={(v) => field.onChange(v || "")}
                loading={isCoversLoading}
              />
            )}
          />

          {sectionTitle("Hero section")}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Subtitle"
              placeholder="Comprehensive trek coverage..."
              {...register("subtitle")}
            />
            <TextInput
              label="Discount Label"
              placeholder="Save 20%"
              {...register("discount_label")}
            />
          </SimpleGrid>
          <TextInput
            label="Promo Banner Text"
            placeholder="Limited time offer..."
            {...register("promo_banner")}
          />

          {sectionTitle("Trek details")}
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <TextInput
              label="Region"
              placeholder="Nepal Himalaya"
              {...register("region")}
            />
            <TextInput
              label="Activity Type"
              placeholder="High-altitude trekking"
              {...register("activity_type")}
            />
            <TextInput
              label="Max Altitude"
              placeholder="5,364 m"
              {...register("max_altitude")}
            />
            <TextInput
              label="Altitude Band"
              placeholder="4,000–5,400 m"
              {...register("altitude_band")}
            />
            <Controller
              name="difficulty"
              control={control}
              render={({ field }) => (
                <Select
                  label="Difficulty"
                  placeholder="-- Select --"
                  data={DIFFICULTY_OPTIONS}
                  value={field.value || null}
                  onChange={(v) => field.onChange(v ?? "")}
                  clearable
                />
              )}
            />
            <TextInput
              label="Season Window"
              placeholder="Mar–May, Sep–Nov"
              {...register("season_window")}
            />
            <TextInput
              label="Access Point"
              placeholder="Lukla Airport"
              {...register("access_point")}
            />
            <TextInput
              label="Terrain"
              placeholder="Rocky trails, glacial moraines"
              {...register("terrain")}
            />
          </SimpleGrid>
          <Textarea
            label="Scope Description"
            placeholder="Multi-day mountain trek"
            minRows={2}
            {...register("scope_description")}
          />

          {sectionTitle("Stats")}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Covered Users"
              placeholder="500K+"
              {...register("stat_covered")}
            />
            <TextInput
              label="Evacuations"
              placeholder="250 Air+Land"
              {...register("stat_evacuation")}
            />
            <TextInput
              label="Wilderness Areas"
              placeholder="1400+"
              {...register("stat_wilderness")}
            />
            <TextInput
              label="Settled Amount"
              placeholder="$4 Million+"
              {...register("stat_settled_amount")}
            />
          </SimpleGrid>

          {sectionTitle("Sales pitch")}
          <Textarea
            label="Pitch Text"
            placeholder="Why this plan is perfect for this trek..."
            minRows={4}
            {...register("pitch_text")}
          />

          {sectionTitle("Why ASC360")}
          <Textarea
            label="Why ASC360 Text"
            placeholder="ASC360 is the most trusted provider..."
            minRows={4}
            {...register("why_asc360_text")}
          />
          <Textarea
            label="Why ASC360 Bullets (comma separated)"
            placeholder="24/7 support, Instant claims, Global coverage"
            minRows={2}
            {...register("why_asc360_bullets")}
          />

          {sectionTitle("Key features")}
          <Textarea
            label="Key Features (comma separated)"
            placeholder="Helicopter evacuation, AMS cover, ..."
            minRows={3}
            {...register("key_features")}
          />

          {sectionTitle("Notes / legal")}
          <Textarea
            label="Important Notes (comma separated)"
            placeholder="Exclusions, eligibility..."
            minRows={2}
            {...register("important_notes")}
          />
          <Textarea
            label="Conditional Notes (comma separated)"
            placeholder="When benefits apply..."
            minRows={2}
            {...register("conditional_notes")}
          />

          <Group
            justify="flex-end"
            mt="md"
            pt="md"
            px={4}
            pb={4}
            style={{
              borderTop: "1px solid #e2e8f0",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <Button variant="default" type="button" onClick={onClose} radius="md">
              Cancel
            </Button>
            <Button
              variant="gradient"
              type="submit"
              loading={isSubmitting}
              radius="md"
            >
              Save Profile
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ExplorePlanProfileModalForm;